import { supabase } from '../../core/config/supabase';
import { NotificationEntity, NotificationPreferenceEntity } from '../entities/NotificationEntity';

export class NotificationDataSource {
  async registerPushToken(userId: string, token: string, deviceName?: string): Promise<void> {
    // Registration runs on every app start: an existing (user_id, token) row is
    // left untouched (ON CONFLICT DO NOTHING) instead of updated.
    const { error } = await supabase
      .from('push_tokens')
      .upsert(
        { user_id: userId, token, device_name: deviceName },
        { onConflict: 'user_id,token', ignoreDuplicates: true }
      );

    if (error) throw new Error(error.message);
  }

  async removePushToken(userId: string, token: string): Promise<void> {
    const { error } = await supabase
      .from('push_tokens')
      .delete()
      .eq('user_id', userId)
      .eq('token', token);

    if (error) throw new Error(error.message);
  }

  async getNotifications(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<NotificationEntity[]> {
    const { data, error } = await supabase.rpc('get_user_notifications', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) throw new Error(error.message);
    return (data as NotificationEntity[]) || [];
  }

  async getUnreadCount(userId: string): Promise<number> {
    const { data, error } = await supabase.rpc('get_unread_notification_count', {
      target_user_id: userId,
    });

    if (error) throw new Error(error.message || 'get_unread_notification_count failed');
    return (data as number) || 0;
  }

  async markAsRead(notificationId: string): Promise<void> {
    const { error } = await supabase.rpc('mark_notification_read', {
      notification_id: notificationId,
    });

    if (error) throw new Error(error.message);
  }

  async markAllAsRead(userId: string): Promise<void> {
    const { error } = await supabase.rpc('mark_all_notifications_read', {
      target_user_id: userId,
    });

    if (error) throw new Error(error.message);
  }

  async getPreferences(userId: string): Promise<NotificationPreferenceEntity[]> {
    const { data, error } = await supabase.rpc('get_notification_preferences', {
      target_user_id: userId,
    });

    if (error) throw new Error(error.message);
    return (data as NotificationPreferenceEntity[]) || [];
  }

  async updatePreference(userId: string, type: string, enabled: boolean): Promise<void> {
    const { error } = await supabase.rpc('upsert_notification_preference', {
      target_user_id: userId,
      p_type: type,
      p_enabled: enabled,
    });

    if (error) throw new Error(error.message);
  }

  async sendNotification(
    userId: string,
    type: string,
    title: string,
    body: string,
    data?: Record<string, string>
  ): Promise<void> {
    const { error } = await supabase.functions.invoke('send-push-notification', {
      body: { userId, type, title, body, data },
    });

    if (error) throw new Error(error.message);
  }
}
